import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import { TextInput } from "@sinch/components/form/TextInput/TextInput";
import React, { ChangeEventHandler, FC, KeyboardEventHandler, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InsertImagePayload } from "../plugins/ImagesPlugin";

const titleId = "insert-image-url-dialog-title";

interface InsertImageUrlDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: InsertImagePayload) => void;
}

/**
 * Small dialog asking for an image URL (required) and an alt text (optional).
 *
 * It is a controlled, editor-agnostic component - the caller decides what to do with the confirmed
 * payload (see InsertImage, which dispatches it as INSERT_IMAGE_COMMAND).
 */
export const InsertImageUrlDialog: FC<InsertImageUrlDialogProps> = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const urlInputRef = useRef<HTMLInputElement>(null);

  const [url, setUrl] = useState("");
  const [altText, setAltText] = useState("");

  const canInsert = url.trim() !== "";

  const handleUrlChange = useCallback<ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
    (event) => setUrl(event.target.value),
    []
  );

  const handleAltTextChange = useCallback<ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
    (event) => setAltText(event.target.value),
    []
  );

  const handleClose = useCallback(() => {
    setUrl("");
    setAltText("");
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (url.trim() === "") {
      return;
    }

    onConfirm({ altText: altText.trim(), src: url.trim() });
    setUrl("");
    setAltText("");
  }, [altText, onConfirm, url]);

  const handleKeyDown = useCallback<KeyboardEventHandler>(
    (event) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      handleConfirm();
    },
    [handleConfirm]
  );

  // The toolbar menu releases its focus trap in the same commit as this dialog mounts,
  // so autoFocus alone is not reliable - focus once the enter transition is done.
  const handleEntered = useCallback(() => {
    urlInputRef.current?.focus();
  }, []);

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={handleClose}
      open={open}
      slotProps={{ transition: { onEntered: handleEntered } }}
    >
      <DialogTitle id={titleId}>{t("TextEditor.insertImageFromUrl")}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <TextInput
            autoFocus
            fullWidth
            inputRef={urlInputRef}
            label={t("TextEditor.imageUrl")}
            onChange={handleUrlChange}
            onKeyDown={handleKeyDown}
            placeholder="https://"
            required
            value={url}
          />
          <TextInput
            fullWidth
            label={t("TextEditor.imageAltText")}
            onChange={handleAltTextChange}
            onKeyDown={handleKeyDown}
            value={altText}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t("cancel")}</Button>
        <Button disabled={!canInsert} onClick={handleConfirm} variant="contained">
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
